import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axiosInstance from './axiosInstance';
import ModelPicker from './ModelPicker';

/**
 * Size a scroll container so the PAGE never grows a second scrollbar.
 *
 * The whole-page scroller on this app is <body>, not <html> — index.css pins
 * `html, body, #root { height: 100% }`, so overflow lands on body. We measure
 * BOTH and take the max, then trim the element by exactly the overflow, then
 * converge over a few frames (trimming shifts layout slightly). Measuring
 * documentElement alone silently misses the body scrollbar entirely.
 */
// Default reserve covers the page's bottom padding (.srf-container pad ~24px +
// card border) so the fitted table never pushes the body past the viewport —
// otherwise the page grows a second vertical scrollbar on top of the table's.
export function useFitToViewport(ref, bottomReserve = 28) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let raf;
    const overflowNow = () =>
      Math.max(document.documentElement.scrollHeight, document.body.scrollHeight) - window.innerHeight;
    const correct = (pass) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const ov = overflowNow();
        if (ov > 1) {
          // maxHeight is border-box (global box-sizing:border-box), so trimming
          // offsetHeight by the overflow removes exactly that from the page.
          el.style.maxHeight = `${Math.max(240, el.offsetHeight - ov)}px`;
          if (pass < 3) correct(pass + 1);
        }
      });
    };
    const fit = () => {
      const top = el.getBoundingClientRect().top; // viewport-relative
      el.style.maxHeight = `${Math.max(240, window.innerHeight - top - bottomReserve)}px`;
      correct(0);
    };
    fit();
    window.addEventListener('resize', fit);
    return () => { window.removeEventListener('resize', fit); cancelAnimationFrame(raf); };
  });
}

// Per-week fulfilment status: a valued cell cycles outstanding → confirmed →
// fulfilled on click. Colour is the primary signal; the short label + tooltip
// name it.
const STATUS_ORDER = ['outstanding', 'confirmed', 'fulfilled'];
const STATUS_LABEL = { outstanding: 'Outstanding', confirmed: 'Confirmed', fulfilled: 'Fulfilled' };
const STATUS_ABBR  = { outstanding: 'Out', confirmed: 'Conf', fulfilled: 'Ful' };
const nextStatus = (s) => STATUS_ORDER[(STATUS_ORDER.indexOf(s) + 1) % STATUS_ORDER.length];

/**
 * ShortTermForecast — 16-week rolling weekly forecast grid for a company.
 * Rows = products (models). Columns = weeks (Monday of each week).
 */
function ShortTermForecast({ company, selectedUserId, models, onOpenSnapshot }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [pastWeeksRevealed, setPastWeeksRevealed] = useState(false);
  const [showAddRow, setShowAddRow] = useState(false);
  const scrollRef = useRef(null);
  useFitToViewport(scrollRef);

  const PAST_WEEKS_TO_REVEAL = 8;

  // Monday of the week containing `date`, returned as YYYY-MM-DD
  const mondayOf = (date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0=Sun..6=Sat
    const diff = (day === 0 ? -6 : 1 - day);
    d.setDate(d.getDate() + diff);
    return d.toISOString().slice(0, 10);
  };

  const currentWeek = useMemo(() => mondayOf(new Date()), []);

  // 16 rolling weeks (current + 15 forward), optionally with 8 past prepended
  const displayWeeks = useMemo(() => {
    const weeks = [];
    const start = new Date(currentWeek);
    const offset = pastWeeksRevealed ? -PAST_WEEKS_TO_REVEAL : 0;
    const count = 16 + (pastWeeksRevealed ? PAST_WEEKS_TO_REVEAL : 0);
    for (let i = 0; i < count; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + (offset + i) * 7);
      weeks.push(d.toISOString().slice(0, 10));
    }
    return weeks;
  }, [currentWeek, pastWeeksRevealed]);

  // ISO week number for column label
  const weekLabel = (weekStart) => {
    const d = new Date(weekStart);
    const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = target.getUTCDay() || 7;
    target.setUTCDate(target.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
    const weekNum = Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
    const monthShort = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
    return { line1: `W${weekNum}`, line2: `${monthShort} ${d.getDate()}` };
  };

  const fetchLines = useCallback(async () => {
    if (!company) return;
    setLoading(true);
    try {
      const params = {};
      if (selectedUserId && selectedUserId !== 'all') params.userId = selectedUserId;
      const res = await axiosInstance.get(`/forecast/short-term/${encodeURIComponent(company)}`, { params });
      setLines(res.data.lines || []);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Failed to load short-term forecast');
    } finally {
      setLoading(false);
    }
  }, [company, selectedUserId]);

  useEffect(() => { fetchLines(); }, [fetchLines]);

  const handleSetQuantity = async (lineId, weekStart, quantity) => {
    setEditingField(null);
    try {
      await axiosInstance.put(`/forecast/short-term/${lineId}/quantity`, { weekStart, quantity });
      fetchLines();
    } catch (err) {
      console.error(err);
      setError('Failed to update quantity');
    }
  };

  const handleSetStatus = async (lineId, weekStart, status) => {
    try {
      await axiosInstance.put(`/forecast/short-term/${lineId}/status`, { weekStart, status });
      fetchLines();
    } catch (err) {
      console.error(err);
      setError('Failed to update status');
    }
  };

  // Batch add from the picker: POST each selected SKU, then refresh once
  const handleAddLines = async (sels) => {
    const errs = [];
    for (const sel of sels) {
      try {
        await axiosInstance.post('/forecast/short-term', {
          company,
          productName: sel.name,
          modelId: sel.modelId,
          bezel: sel.bezel,
          modelCode: sel.modelCode
        });
      } catch (err) {
        errs.push(`${sel.name}: ${err.response?.data?.error || 'failed'}`);
      }
    }
    setShowAddRow(false);
    setError(errs.length ? errs.join(' · ') : null);
    fetchLines();
  };

  const handleDeleteLine = async (lineId) => {
    if (!window.confirm('Delete this line?')) return;
    try {
      await axiosInstance.delete(`/forecast/short-term/${lineId}`);
      fetchLines();
    } catch (err) {
      console.error(err);
      setError('Failed to delete line');
    }
  };

  const formatNumber = (n) => (n === null || n === undefined ? '-' : n.toLocaleString());

  const getQty = (line, weekStart) =>
    line.weeklyData?.find(w => w.weekStart === weekStart)?.quantity || 0;

  const getStatus = (line, weekStart) =>
    line.weeklyData?.find(w => w.weekStart === weekStart)?.status || 'outstanding';

  // Total = only the weeks currently on screen, so the number always matches
  // the visible cells (old quantities that rolled out of the 16-wk window
  // used to inflate this invisibly).
  const lineTotal = (line) =>
    (line.weeklyData || [])
      .filter(w => displayWeeks.includes(w.weekStart))
      .reduce((s, w) => s + (w.quantity || 0), 0);

  return (
    <div className="srf-store-types-section">
      <div className="srf-section-header">
        <h2>Short-Term Forecast — {company}</h2>
        <div className="srf-section-header-right">
          <button
            className="srf-btn srf-btn-primary"
            onClick={() => setShowAddRow(true)}
          >
            + Add Model
          </button>
        </div>
      </div>

      {error && <div className="srf-error" style={{ marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div className="srf-loading">Loading...</div>
      ) : (
        <div className="srf-store-types-list">
          <div className="srf-store-type-card">
            {/* Header row of week labels */}
            <div className="srf-monthly-grid">
              <div className="srf-st-scroll" ref={scrollRef}>
              <div className="srf-st-row srf-st-header-row">
                <div className="srf-st-product-cell srf-st-header-cell">Model</div>
                {displayWeeks.map(w => {
                  const lbl = weekLabel(w);
                  const isPast = w < currentWeek;
                  const isCurrent = w === currentWeek;
                  const trigger = !!onOpenSnapshot;
                  return (
                    <div
                      key={w}
                      className={`srf-st-week-cell srf-st-header-cell ${isPast ? 'srf-month-past' : ''} ${isCurrent ? 'srf-week-current' : ''} ${trigger ? 'srf-week-trigger' : ''}`}
                      onClick={trigger ? () => onOpenSnapshot(w) : undefined}
                      role={trigger ? 'button' : undefined}
                      tabIndex={trigger ? 0 : undefined}
                      title={trigger ? `Open snapshot of ${lbl.line1}` : undefined}
                      onKeyDown={trigger ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenSnapshot(w); } } : undefined}
                    >
                      <div className="srf-st-week-label-1">{lbl.line1}</div>
                      <div className="srf-st-week-label-2">{lbl.line2}</div>
                    </div>
                  );
                })}
                <div className="srf-st-total-cell srf-st-header-cell">Total</div>
                <div className="srf-st-action-cell srf-st-header-cell"></div>
              </div>

              {/* Data rows */}
              {lines.length === 0 && !showAddRow && (
                <div className="srf-no-data" style={{ padding: 16 }}>
                  No models yet. Click "+ Add Model" to start.
                </div>
              )}

              {lines.map(line => (
                <div key={line._id} className="srf-st-row">
                  <div className="srf-st-product-cell">
                    <div
                      className="srf-st-product-name"
                      title={`${line.productName}${line.bezel ? ` (${line.bezel === 'white' ? 'White' : line.bezel === 'red' ? 'Red' : 'Black'})` : ''}${line.modelCode ? ` - ${line.modelCode}` : ''}`}
                    >
                      {line.productName}{line.bezel ? ` (${line.bezel === 'white' ? 'White' : line.bezel === 'red' ? 'Red' : 'Black'})` : ''}
                    </div>
                    {line.modelCode && (
                      <div className="srf-st-product-code">{line.modelCode}</div>
                    )}
                  </div>
                  {displayWeeks.map(w => {
                    const qty = getQty(line, w);
                    const isEditing = editingField === `${line._id}-${w}`;
                    const isPast = w < currentWeek;
                    const isCurrent = w === currentWeek;
                    return (
                      <div key={w} className={`srf-st-week-cell ${isPast ? 'srf-month-past' : ''} ${isCurrent ? 'srf-week-current' : ''}`}>
                        {isEditing ? (
                          <input
                            type="number"
                            className="srf-month-input"
                            value={editValue}
                            autoFocus
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={() => handleSetQuantity(line._id, w, parseFloat(editValue) || 0)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSetQuantity(line._id, w, parseFloat(editValue) || 0);
                              if (e.key === 'Escape') setEditingField(null);
                            }}
                          />
                        ) : (
                          <>
                            <div
                              className={`srf-month-value ${qty > 0 ? 'has-value' : ''}`}
                              onClick={() => { setEditingField(`${line._id}-${w}`); setEditValue(qty || ''); }}
                            >
                              {qty > 0 ? formatNumber(qty) : '-'}
                            </div>
                            {qty > 0 && (() => {
                              const status = getStatus(line, w);
                              return (
                                <button
                                  type="button"
                                  className={`srf-status-chip srf-status-${status}`}
                                  title={`${STATUS_LABEL[status]} — click to cycle`}
                                  onClick={(e) => { e.stopPropagation(); handleSetStatus(line._id, w, nextStatus(status)); }}
                                >
                                  {STATUS_ABBR[status]}
                                </button>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    );
                  })}
                  <div className="srf-st-total-cell">
                    <div className="srf-month-value has-value">{formatNumber(lineTotal(line))}</div>
                  </div>
                  <div className="srf-st-action-cell">
                    <button
                      className="srf-btn srf-btn-sm srf-btn-danger"
                      onClick={() => handleDeleteLine(line._id)}
                      title="Delete line"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
              </div>

              {/* Add-row inline form */}
              {showAddRow && (
                <div className="srf-st-picker-wrap">
                  <ModelPicker
                    models={models}
                    existingKeys={lines.map(l => `${l.productName}|${l.bezel}`)}
                    onAddMany={handleAddLines}
                    onClose={() => setShowAddRow(false)}
                  />
                </div>
              )}

              <button
                type="button"
                className="srf-prev-months-toggle"
                onClick={() => setPastWeeksRevealed(v => !v)}
                title={pastWeeksRevealed ? 'Hide past weeks' : 'Show past weeks'}
              >
                {pastWeeksRevealed ? '‹ hide past' : '‹ prev'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShortTermForecast;
